// src/components/StyleManager.js
import React, { useState } from "react";
import {
  Package,
  Plus,
  Pencil,
  Trash2,
  GripVertical,
  ChevronsUp,
  ChevronsDown,
  Check,
  X,
  Info
} from "lucide-react";
import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  writeBatch,
} from "firebase/firestore";
import { PRODUCT_CONFIG, APRON_CATEGORIES } from "../data";
import { getEdgingExtraCost } from "../useCalculator";

const StyleManagerComponent = ({
  productType,
  styleDataMap,
  cushionThicknesses,
  setCushionThicknesses,
  db,
  appId,
  IS_TEST_MODE,
  showToast,
}) => {
  // 驗證數字輸入：必須為有限的非負數字（擋掉空字串→NaN、負數）
  const toValidNumber = (raw, label) => {
    const n = parseFloat(raw);
    if (!Number.isFinite(n) || n < 0) {
      showToast?.(`請為「${label}」輸入有效的非負數字`, "error");
      return null;
    }
    return n;
  };
  const isCushion = productType === "cushion";
  const isDust = productType === "dust_mite";
  const isApron = productType === "apron";
  // 有運費率欄位的產品類型（面積 × 運費率計算的品項）
  const hasFreightRate = ["sofa_mat", "carpet", "edging_carpet", "wall_sticker"].includes(productType);
  // 固定加價（RMB/件）：目前僅壓邊地毯使用
  const hasExtraCost = productType === "edging_carpet";

  const target = styleDataMap[productType];

  const [newStyleName, setNewStyleName] = useState("");
  const [newStyleCategory, setNewStyleCategory] = useState("圍裙");
  const [newStyleCost, setNewStyleCost] = useState("");
  const [newStyleFreightRate, setNewStyleFreightRate] = useState("");
  const [newStyleExtra, setNewStyleExtra] = useState("");
  const [editingStyleId, setEditingStyleId] = useState(null);
  const [editingStyleName, setEditingStyleName] = useState("");
  const [editingStyleCost, setEditingStyleCost] = useState("");
  const [editingStyleFreightRate, setEditingStyleFreightRate] = useState("");
  const [editingStyleExtra, setEditingStyleExtra] = useState("");

  const [newThick, setNewThick] = useState({
    cm: "",
    m35: "",
    m45: "",
    t35: "",
    t45: "",
  });
  const [editingThickId, setEditingThickId] = useState(null);
  const [edThick, setEdThick] = useState({
    cm: "",
    m35: "",
    m45: "",
    t35: "",
    t45: "",
  });

  const handleAddStyle = async () => {
    if (!target || !db) return;
    if (!newStyleName.trim()) {
      showToast?.("請輸入款式名稱", "error");
      return;
    }
    const cost = toValidNumber(newStyleCost, isApron ? "單價" : "成本");
    if (cost === null) return;
    const maxOrder =
      (target.styles || []).length > 0
        ? Math.max(...target.styles.map((i) => i.order || 0))
        : 0;
    const data = {
      name: newStyleName.trim(),
      cost,
      order: maxOrder + 1,
    };
    if (isApron) data.category = newStyleCategory;
    if (hasFreightRate && newStyleFreightRate !== "") {
      const fr = toValidNumber(newStyleFreightRate, "運費率");
      if (fr === null) return;
      data.freightRate = fr;
    }
    if (hasExtraCost && newStyleExtra !== "") {
      const ex = toValidNumber(newStyleExtra, "固定加價");
      if (ex === null) return;
      data.extraCost = ex;
    }

    try {
      await addDoc(
        collection(db, "artifacts", appId, "public", "data", target.col),
        data
      );
      showToast?.("已新增款式");
    } catch (e) {
      console.error("Add style error:", e);
      showToast?.("新增失敗，請重試", "error");
      return;
    }
    setNewStyleName("");
    setNewStyleCost("");
    setNewStyleFreightRate("");
    setNewStyleExtra("");
  };

  const handleUpdateStyle = async () => {
    if (!target || !db) return;
    if (!editingStyleName.trim()) {
      showToast?.("請輸入款式名稱", "error");
      return;
    }
    const cost = toValidNumber(editingStyleCost, isApron ? "單價" : "成本");
    if (cost === null) return;
    const updateData = {
      name: editingStyleName.trim(),
      cost,
    };
    if (isApron) updateData.category = newStyleCategory;
    if (hasFreightRate && editingStyleFreightRate !== "") {
      const fr = toValidNumber(editingStyleFreightRate, "運費率");
      if (fr === null) return;
      updateData.freightRate = fr;
    }
    if (hasExtraCost && editingStyleExtra !== "") {
      const ex = toValidNumber(editingStyleExtra, "固定加價");
      if (ex === null) return;
      updateData.extraCost = ex;
    }
    try {
      await updateDoc(
        doc(db, "artifacts", appId, "public", "data", target.col, editingStyleId),
        updateData
      );
      showToast?.("已更新款式");
    } catch (e) {
      console.error("Update style error:", e);
      showToast?.("更新失敗，請重試", "error");
      return;
    }
    setEditingStyleId(null);
    setEditingStyleFreightRate("");
  };

  const handleDeleteStyle = async (id) => {
    if (confirm("刪除?") && db) {
      await deleteDoc(
        doc(db, "artifacts", appId, "public", "data", target.col, id)
      );
    }
  };

  const handleMoveItem = async (idx, dir) => {
    if (!target) return;
    const list = [...(target.styles || [])];
    if (dir === "up" && idx > 0)
      [list[idx], list[idx - 1]] = [list[idx - 1], list[idx]];
    else if (dir === "down" && idx < list.length - 1)
      [list[idx], list[idx + 1]] = [list[idx + 1], list[idx]];
    else return;

    target.set(list); // Optimistic update
    if (db) {
      const b = writeBatch(db);
      list.forEach((d, i) =>
        b.update(
          doc(db, "artifacts", appId, "public", "data", target.col, d.id),
          { order: i }
        )
      );
      await b.commit();
    }
  };

  // 驗證厚度物件的所有數字欄位；回傳 parse 後的物件，或 null（表示驗證失敗）
  const parseThickFields = (obj) => {
    const labels = { cm: "厚度", m35: "35D一般", m45: "45D一般", t35: "35D科技", t45: "45D科技" };
    const parsed = {};
    for (const [k, v] of Object.entries(obj)) {
      if (k === "id" || k === "order") continue; // 保留非數值欄位不動
      const n = toValidNumber(v, labels[k] || k);
      if (n === null) return null;
      parsed[k] = n;
    }
    return parsed;
  };

  const handleAddThick = async () => {
    if (!db) return;
    const parsed = parseThickFields(newThick);
    if (parsed === null) return;
    await addDoc(
      collection(db, "artifacts", appId, "public", "data", "cushion_thicknesses"),
      parsed
    );
    setNewThick({ cm: "", m35: "", m45: "", t35: "", t45: "" });
  };

  const handleUpdateThick = async () => {
    if (!db) return;
    const parsed = parseThickFields(edThick);
    if (parsed === null) return;
    await updateDoc(
      doc(db, "artifacts", appId, "public", "data", "cushion_thicknesses", editingThickId),
      parsed
    );
    setEditingThickId(null);
  };

  const handleDeleteThick = async (id) => {
    if (db && confirm("刪除?"))
      await deleteDoc(
        doc(db, "artifacts", appId, "public", "data", "cushion_thicknesses", id)
      );
  };

  // 尋找代表抱枕的特殊設定 (cm = 0)
  const pillowConfig = cushionThicknesses.find(t => t.cm === 0);
  const normalCushions = cushionThicknesses.filter(t => t.cm !== 0);

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
      <div className="p-6 border-b border-slate-100 flex items-center justify-between">
        <h2 className="text-lg font-semibold text-slate-700 flex items-center gap-2">
          <Package size={20} />{" "}
          {isCushion
            ? "坐墊/抱枕 價格設定"
            : `${PRODUCT_CONFIG[productType]?.label} 款式管理`}
        </h2>
      </div>
      <div className="p-6">
        {isDust ? (
          <div className="text-center py-12 text-slate-400 text-sm bg-slate-50 rounded-xl border border-dashed border-slate-200">
            除塵墊無須設定款式
          </div>
        ) : isCushion ? (
          <div className="space-y-8">
            
            {/* === 抱枕專屬設定區塊 === */}
            <div className="space-y-3">
               <div className="flex items-center gap-2 text-slate-700 font-bold pb-2 border-b">
                 <span>抱枕套設定</span>
                 <span className="text-[10px] bg-blue-100 text-blue-600 px-2 py-0.5 rounded flex items-center gap-1">
                    <Info size={10} /> 系統預設以 0cm 代表抱枕參數
                 </span>
               </div>
               
               {pillowConfig ? (
                 <div className="grid grid-cols-5 gap-2 text-center text-sm font-medium items-center p-3 bg-amber-50 border border-amber-200 rounded-xl">
                    {editingThickId === pillowConfig.id ? (
                        <>
                          <div className="flex flex-col gap-1 text-xs">
                             <span className="text-slate-500">一般抱枕套</span>
                             <input type="number" value={edThick.m35} onChange={e => setEdThick({...edThick, m35: e.target.value})} className="p-1 rounded border text-center" />
                          </div>
                          <div className="flex flex-col gap-1 text-xs">
                             <span className="text-slate-500">科技布抱枕套</span>
                             <input type="number" value={edThick.t35} onChange={e => setEdThick({...edThick, t35: e.target.value})} className="p-1 rounded border text-center" />
                          </div>
                          <div className="flex flex-col gap-1 text-xs">
                             <span className="text-slate-500">一般套+枕心</span>
                             <input type="number" value={edThick.m45} onChange={e => setEdThick({...edThick, m45: e.target.value})} className="p-1 rounded border text-center" />
                          </div>
                          <div className="flex flex-col gap-1 text-xs">
                             <span className="text-slate-500">科技布套+枕心</span>
                             <input type="number" value={edThick.t45} onChange={e => setEdThick({...edThick, t45: e.target.value})} className="p-1 rounded border text-center" />
                          </div>
                          <div className="flex gap-1 justify-center mt-4">
                              <button onClick={handleUpdateThick} className="bg-amber-500 text-white p-1.5 rounded"><Check size={16} /></button>
                              <button onClick={() => setEditingThickId(null)} className="bg-gray-300 p-1.5 rounded"><X size={16} /></button>
                          </div>
                        </>
                    ) : (
                        <>
                          <div className="flex flex-col">
                             <span className="text-xs text-slate-500">一般抱枕套</span>
                             <span className="text-amber-700 font-bold">¥{pillowConfig.m35}</span>
                          </div>
                          <div className="flex flex-col">
                             <span className="text-xs text-slate-500">科技布抱枕套</span>
                             <span className="text-amber-700 font-bold">¥{pillowConfig.t35}</span>
                          </div>
                          <div className="flex flex-col">
                             <span className="text-xs text-slate-500">一般套+枕心</span>
                             <span className="text-amber-700 font-bold">¥{pillowConfig.m45}</span>
                          </div>
                          <div className="flex flex-col">
                             <span className="text-xs text-slate-500">科技布套+枕心</span>
                             <span className="text-amber-700 font-bold">¥{pillowConfig.t45}</span>
                          </div>
                          <div>
                             <button onClick={() => { setEditingThickId(pillowConfig.id); setEdThick(pillowConfig); }} className="text-amber-600 bg-white border border-amber-200 px-3 py-1.5 rounded shadow-sm hover:bg-amber-100 flex items-center justify-center mx-auto gap-1 text-xs">
                                <Pencil size={12}/> 編輯
                             </button>
                          </div>
                        </>
                    )}
                 </div>
               ) : (
                 <div className="text-sm text-slate-500 bg-slate-50 p-4 rounded text-center border">
                    找不到抱枕設定檔。您可以新增一個「厚度設為 0」的設定來啟動抱枕計價。
                 </div>
               )}
            </div>

            {/* === 坐墊厚度設定區塊 === */}
            <div className="space-y-4">
              <div className="text-slate-700 font-bold pb-2 border-b">
                 常規坐墊厚度參數
              </div>
              <div className="grid grid-cols-6 gap-2 text-xs font-bold text-slate-500 px-2 text-center">
                <div>厚度(cm)</div>
                <div>35D一般</div>
                <div>35D科技</div>
                <div>45D一般</div>
                <div>45D科技</div>
                <div>操作</div>
              </div>
              <div className="grid grid-cols-6 gap-2 items-center bg-slate-50 p-2 rounded-lg border">
                {["cm", "m35", "t35", "m45", "t45"].map((f) => (
                  <input
                    key={f}
                    type="number"
                    placeholder={f}
                    value={editingThickId && edThick.cm !== 0 ? edThick[f] : newThick[f]}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (editingThickId && edThick.cm !== 0) {
                         setEdThick((p) => ({ ...p, [f]: v }));
                      } else {
                         setNewThick((p) => ({ ...p, [f]: v }));
                      }
                    }}
                    className="p-2 rounded border w-full outline-none text-center"
                  />
                ))}
                {editingThickId && edThick.cm !== 0 ? (
                  <div className="flex gap-1">
                    <button
                      onClick={handleUpdateThick}
                      className="bg-amber-500 text-white p-2 rounded w-full"
                    >
                      <Check size={16} />
                    </button>
                    <button
                      onClick={() => setEditingThickId(null)}
                      className="bg-gray-300 p-2 rounded w-full"
                    >
                      <X size={16} />
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={handleAddThick}
                    className="bg-emerald-600 text-white p-2 rounded w-full"
                  >
                    <Plus size={16} />
                  </button>
                )}
              </div>
              <div className="space-y-2 max-h-[400px] overflow-y-auto pr-1">
                {normalCushions.sort((a,b) => a.cm - b.cm).map((t) => (
                  <div
                    key={t.id}
                    className="grid grid-cols-6 gap-2 items-center p-2 border rounded-lg text-sm text-center font-medium text-slate-600 hover:bg-slate-50"
                  >
                    <div>{t.cm}cm</div>
                    <div>¥{t.m35}</div>
                    <div>¥{t.t35}</div>
                    <div>¥{t.m45}</div>
                    <div>¥{t.t45}</div>
                    <div className="flex gap-1 justify-center">
                      <button
                        onClick={() => {
                          setEditingThickId(t.id);
                          setEdThick(t);
                        }}
                        className="text-amber-500 p-1"
                      >
                        <Pencil size={16} />
                      </button>
                      <button
                        onClick={() => handleDeleteThick(t.id)}
                        className="text-red-400 p-1"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

          </div>
        ) : (
          <>
            <div className="flex gap-2 mb-4 items-end bg-slate-50 p-4 rounded-xl border">
              {isApron && (
                <div className="w-24">
                  <label className="text-xs text-slate-500">分類</label>
                  <select
                    value={newStyleCategory}
                    onChange={(e) => setNewStyleCategory(e.target.value)}
                    className="w-full p-2 border rounded outline-none bg-white"
                  >
                    {APRON_CATEGORIES.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <div className="flex-1">
                <label className="text-xs text-slate-500">款式名稱</label>
                <input
                  type="text"
                  value={editingStyleId ? editingStyleName : newStyleName}
                  onChange={(e) =>
                    editingStyleId
                      ? setEditingStyleName(e.target.value)
                      : setNewStyleName(e.target.value)
                  }
                  className="w-full p-2 border rounded outline-none"
                />
              </div>
              <div className="w-24">
                <label className="text-xs text-slate-500">
                  {isApron ? "單價 (TWD)" : "成本 (RMB)"}
                </label>
                <input
                  type="number"
                  value={editingStyleId ? editingStyleCost : newStyleCost}
                  onChange={(e) =>
                    editingStyleId
                      ? setEditingStyleCost(e.target.value)
                      : setNewStyleCost(e.target.value)
                  }
                  className="w-full p-2 border rounded outline-none"
                />
              </div>
              {hasFreightRate && (
                <div className="w-28">
                  <label className="text-xs text-slate-500">運費率 (RMB/m²)</label>
                  <input
                    type="number"
                    placeholder="如 36 或 52"
                    value={editingStyleId ? editingStyleFreightRate : newStyleFreightRate}
                    onChange={(e) =>
                      editingStyleId
                        ? setEditingStyleFreightRate(e.target.value)
                        : setNewStyleFreightRate(e.target.value)
                    }
                    className="w-full p-2 border rounded outline-none"
                  />
                </div>
              )}
              {hasExtraCost && (
                <div className="w-28">
                  <label className="text-xs text-slate-500">固定加價 (RMB/件)</label>
                  <input
                    type="number"
                    placeholder="如 150，無則留空"
                    value={editingStyleId ? editingStyleExtra : newStyleExtra}
                    onChange={(e) =>
                      editingStyleId
                        ? setEditingStyleExtra(e.target.value)
                        : setNewStyleExtra(e.target.value)
                    }
                    className="w-full p-2 border rounded outline-none"
                  />
                </div>
              )}
              {editingStyleId ? (
                <div className="flex gap-1">
                  <button
                    onClick={handleUpdateStyle}
                    className="bg-amber-600 text-white p-2 rounded hover:bg-amber-700"
                  >
                    更新
                  </button>
                  <button
                    onClick={() => {
                      setEditingStyleId(null);
                      setEditingStyleName("");
                      setEditingStyleCost("");
                    }}
                    className="bg-slate-200 text-slate-600 p-2 rounded"
                  >
                    取消
                  </button>
                </div>
              ) : (
                <button
                  onClick={handleAddStyle}
                  className="bg-emerald-600 text-white px-4 py-2 rounded hover:bg-emerald-700 flex items-center gap-1"
                >
                  <Plus size={18} /> 新增
                </button>
              )}
            </div>
            <div className="space-y-2 max-h-[600px] overflow-y-auto">
              {(target?.styles || []).map((s, idx) => (
                <div
                  key={s.id}
                  className={`flex items-center justify-between p-3 rounded-xl border ${
                    editingStyleId === s.id
                      ? "bg-amber-50 border-amber-300"
                      : "bg-white hover:shadow-sm"
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className="text-slate-300">
                      <GripVertical size={20} />
                    </div>
                    <div>
                      <div className="font-bold text-slate-700">
                        {s.name}{" "}
                        {isApron && (
                          <span className="text-xs font-normal bg-slate-100 px-1 rounded ml-1">
                            {s.category}
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-slate-500">
                        {isApron ? "單價: $" : "成本: ¥"}
                        {s.cost}
                        {hasFreightRate && s.freightRate != null && (
                          <span className="ml-2 text-rose-500">運費率: ¥{s.freightRate}/m²</span>
                        )}
                        {hasFreightRate && s.freightRate == null && (
                          <span className="ml-2 text-amber-500">運費率: 未設定(預設36)</span>
                        )}
                        {hasExtraCost &&
                          getEdgingExtraCost(s) > 0 && (
                            <span className="ml-2 text-indigo-500">
                              固定加價: ¥{getEdgingExtraCost(s)}/件
                              {s.extraCost == null && "（未設定，沿用舊值）"}
                            </span>
                          )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="flex flex-col mr-2">
                      <button
                        onClick={() => handleMoveItem(idx, "up")}
                        className="text-slate-300 hover:text-blue-500"
                      >
                        <ChevronsUp size={14} />
                      </button>
                      <button
                        onClick={() => handleMoveItem(idx, "down")}
                        className="text-slate-300 hover:text-blue-500"
                      >
                        <ChevronsDown size={14} />
                      </button>
                    </div>
                    <button
                      onClick={() => {
                        setEditingStyleId(s.id);
                        setEditingStyleName(s.name);
                        setEditingStyleCost(s.cost);
                        if (hasExtraCost) {
                          const eff = getEdgingExtraCost(s);
                          setEditingStyleExtra(eff ? String(eff) : "");
                        }
                        if (isApron && s.category)
                          setNewStyleCategory(s.category);
                        if (hasFreightRate)
                          setEditingStyleFreightRate(s.freightRate != null ? String(s.freightRate) : "");
                      }}
                      className="p-2 text-slate-400 hover:text-amber-600 bg-slate-50 rounded"
                    >
                      <Pencil size={16} />
                    </button>
                    <button
                      onClick={() => handleDeleteStyle(s.id)}
                      className="p-2 text-slate-400 hover:text-red-500 bg-slate-50 rounded"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default StyleManagerComponent;